import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { alpha } from '@mui/material/styles';
import theme from '../../../theme/theme';
import { useFetch } from '../../../hooks/useFetch';
import { GenFilesApiFactory } from '../api/gen-files.api';
import { useCurrentGenFile } from '../hooks/useCurrentGenFile';
import { GeneralChannelCard } from '../components/GeneralChannelCard/GeneralChannelCard';
import { UploadGenModal } from '../components/UploadGenModal/UploadGenModal';

export function GeneralSettingsPage() {
  const { t } = useTranslation('generalSettings');
  const [client] = useFetch();
  const api = useMemo(() => GenFilesApiFactory(client), [client]);
  const { channelState, loading, error, refresh } = useCurrentGenFile();
  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <Box>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          background: alpha(theme.palette.primary.main, 0.06),
          color: theme.palette.text.body,
        }}
      >
        <Box
          sx={{
            width: theme.avatar.md,
            height: theme.avatar.md,
            borderRadius: theme.borderRadius.md,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            flexShrink: 0,
          }}
        >
          <SettingsOutlinedIcon
            sx={{ color: theme.palette.common.white, fontSize: theme.iconSize.md }}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: theme.typography.employeeName.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.palette.text.body,
            }}
          >
            {t('page.title')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: 3, maxWidth: 720 }}>
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('page.loadError')}
          </Alert>
        )}
        {loading && !channelState && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && !error && (
          <GeneralChannelCard channelState={channelState} onUpload={() => setUploadOpen(true)} />
        )}
      </Box>

      <UploadGenModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        api={api}
        currentVersion={channelState?.current?.version ?? null}
        onSuccess={refresh}
      />
    </Box>
  );
}
